import { StandardRequest, StandardResponse } from "./functions";
import { IUnifiedFilter } from "./filters";

/**
 * Valid types for internal messages.
 */
export type MessageType = "general" | "system" | "alert" | "warning" | "feedback" | "task";

/**
 * Action data payload associated with a Task message.
 */
export interface ITaskActionData {
  taskId: string;
  workflowId?: string;
  memoryId?: string;
  taskState?: string;
  actionTaken?: "accept" | "reject";
}

/**
 * Lifecycle / delivery status of a message.
 */
export type MessageStatus = "draft" | "sent";

/**
 * Recipient state object within a message.
 */
export interface IMessageRecipient {
  contactId: string; // The contact ID for each contact that is a recipient of this message
  groupId?: string; // The group ID for each group that is a recipient of this message
  read: boolean; // If the message has been read by that contact, set to true
  trashed: boolean; // If the message has been moved to trash by that contact, set to true
  readAt?: number; // Optional epoch timestamp when message was marked read
  trashedAt?: number; // Optional epoch timestamp when message was moved to trash
  displayName?: string; // Hydrated recipient contact display name
  email?: string; // Hydrated recipient contact email
  groupName?: string; // Hydrated recipient group name
}

/**
 * @swagger
 *  components:
 *    schemas:
 *      Message:
 *        type: object
 *        required: [ "senderContactId", "type", "subject", "body", "createdAt" ]
 *        properties:
 *          id:
 *            type: string
 *          senderContactId:
 *            type: string
 *          recipients:
 *            type: array
 *            items:
 *              type: object
 *          recipientContactIds:
 *            type: array
 *            items:
 *              type: string
 *          type:
 *            type: string
 *          action:
 *            type: string
 *          actionData:
 *            type: object
 *          subject:
 *            type: string
 *          body:
 *            type: string
 *          createdAt:
 *            type: number
 *          senderUserId:
 *            type: string
 *      MessageModel:
 *        allOf:
 *          - $ref: '#/components/schemas/Message'
 *          - type: object
 *            properties:
 *              senderDisplayName:
 *                type: string
 *              senderEmail:
 *                type: string
 *              isRead:
 *                type: boolean
 *              isTrashed:
 *                type: boolean
 *              isSent:
 *                type: boolean
 *              isRecipient:
 *                type: boolean
 *  */

/**
 * Message document schema representing the raw persisted entity stored in the Firestore 'messages' collection.
 */
export interface IMessage {
  id: string;
  /** Primary foreign key pointing to the sender's Contact ID */
  senderContactId?: string;
  recipients?: IMessageRecipient[]; // Recipients block holding all recipients and their states
  recipientContactIds?: string[]; // Array of recipient contact IDs for Firestore querying
  type: MessageType;
  subject: string;
  body: string;
  action?: "Task" | string; // Action type if this message represents an actionable item
  actionData?: ITaskActionData; // Structured metadata for the action
  createdAt: number;
  sentAt?: number; // Timestamp when message was dispatched/sent
  status?: MessageStatus; // Message lifecycle state ("draft" | "sent")
  senderUserId?: string; // Linked user UID of the message sender if known
  senderEmail?: string; // Author email if attached
}

/**
 * Canonical database table entity type for the 'messages' collection.
 */
export type Message = IMessage;

/**
 * MessageModel represents the hydrated view model of the full message object
 * containing presentation or joined attributes returned to clients and UI views.
 */
export type MessageModel = Message & {
  senderDisplayName?: string; // Dynamically hydrated from sender's Contact record; not persisted in messages collection
  senderEmail?: string; // Dynamically hydrated from sender's Contact record; not persisted in messages collection
  recipientDisplayName?: string; // Dynamically hydrated primary recipient or group name for sent views
  recipientNames?: string[]; // Array of hydrated recipient names (for multi-recipient / group messages)
  targetName?: string; // Presentation target name (single or joined recipients)
  isRead?: boolean; // Evaluated for current user
  isTrashed?: boolean; // Evaluated for current user
  isSent?: boolean; // Client/caller-evaluated: whether this message was sent by the caller
  isRecipient?: boolean; // Client/caller-evaluated: whether this message was addressed to the caller
  targetRole?: string;
};

/**
 * Unified recipient representation combining contacts and groups (both personal and shared).
 */
export interface IMessageRecipientItem {
  id: string;
  targetType: "contact" | "group";
  name: string;
  email?: string;
  description?: string;
  isShared: boolean;
  userRole?: string; // "owner" | "editor" | "viewer"
  userId?: string; // Linked Firebase user ID if known
  label: string; // e.g. "👤 Contact: Alice (Yours)" or "👥 Group: Family (Shared)"
}

/**
 * Request payload to send an internal message.
 */
export interface ISendMessageRequest extends StandardRequest {
  targetType: "contact" | "group";
  targetId: string;
  targetContactId?: string; // Explicit alias for targetId when targetType === "contact"
  targetName: string;
  recipientUserId?: string;
  type: MessageType;
  subject: string;
  body: string;
}

/**
 * Response payload after sending an internal message.
 */
export interface ISendMessageResponse extends StandardResponse {
  sentMessage?: MessageModel;
}

/**
 * Request payload to fetch messages for the current user.
 */
export interface IGetMessagesRequest extends StandardRequest {
  limit?: number;
  type?: MessageType;
  unreadOnly?: boolean;
  folder?: "inbox" | "sent" | "trash" | "all";
  filter?: IUnifiedFilter;
}

/**
 * Response payload containing fetched messages and unread counter.
 */
export interface IGetMessagesResponse extends StandardResponse {
  messages: MessageModel[];
  unreadCount: number;
}

/**
 * Request payload to mark a message as read.
 */
export interface IMarkMessageReadRequest extends StandardRequest {
  messageId: string;
}

/**
 * Response payload after marking message read.
 */
export interface IMarkMessageReadResponse extends StandardResponse {
  unreadCount?: number;
}

/**
 * Request payload to mark a message as unread.
 */
export interface IMarkMessageUnreadRequest extends StandardRequest {
  messageId: string;
}

/**
 * Response payload after marking message unread.
 */
export interface IMarkMessageUnreadResponse extends StandardResponse {
  unreadCount?: number;
}

/**
 * Request payload to move a message to trash.
 */
export interface ITrashMessageRequest extends StandardRequest {
  messageId: string;
}

/**
 * Response payload after moving a message to trash.
 */
export interface ITrashMessageResponse extends StandardResponse {
  success?: boolean;
}

/**
 * Request payload to restore a message from trash.
 */
export interface IRestoreMessageRequest extends StandardRequest {
  messageId: string;
}

/**
 * Response payload after restoring a message from trash.
 */
export interface IRestoreMessageResponse extends StandardResponse {
  success?: boolean;
}

/**
 * Request payload to report inappropriate content to all admins.
 */
export interface IReportInappropriateContentRequest extends StandardRequest {
  reason: string;
  description: string;
  contentUrl?: string;
}

/**
 * Response payload after submitting an inappropriate content report.
 */
export interface IReportInappropriateContentResponse extends StandardResponse {
  reportCount?: number;
}
